import { computed } from "vue";
import { isComposing } from "#ce/utils/events";
import { toBoolean } from "#ce/utils/props";

/**
 * Commits the picker's hinted row under `highlight-first-result`, or its custom-value offer.
 *
 * Enter or Tab in the activator field commits it while the field can narrow the list: the idle hint
 * marks the first row of a list nobody has narrowed, and a readonly activator offers no way to
 * narrow it, so a commit there would replace a selection the user never touched. Enter in the
 * picker's own search bar commits it too. Shift+Tab only moves focus.
 *
 * @param {object} props  Owning component's reactive props; reads `highlightFirstResult` and `inputmode`.
 * @param {{ disabled: import("vue").Ref<boolean>, searchable: import("vue").Ref<boolean>,
 *   hasCustomOffer?: import("vue").Ref<boolean>, keepOpenOnSelect: import("vue").Ref<boolean>, menuOpen: import("vue").Ref<boolean>,
 *   menuShown: import("vue").Ref<boolean>, container: import("vue").Ref<HTMLElement | null>,
 *   findItem: (value: any) => any, getItemValue: (item: any) => any, closeMenu: () => void }} options
 *   `container` is the picker.
 * @returns {{ fieldCommitsHint: import("vue").ComputedRef<boolean>, selectFirstResult: () => any,
 *   handleActivatorEnter: (event: KeyboardEvent) => void, handleActivatorTab: (event: KeyboardEvent) => void,
 *   handleSearchEnter: (event: CustomEvent) => void }}
 */
export function useFirstResultCommit(
  props,
  {
    disabled,
    searchable,
    hasCustomOffer,
    keepOpenOnSelect,
    menuOpen,
    menuShown,
    container,
    findItem,
    getItemValue,
    closeMenu,
  },
) {
  const commitsFirstResult = computed(() => toBoolean(props.highlightFirstResult) || hasCustomOffer?.value);
  const fieldCommitsHint = computed(() => commitsFirstResult.value && (searchable.value || props.inputmode !== "none"));

  // Commits the shown picker's highlighted row through its user path, so it reports like a click.
  // Returns the caller's own item, or null when the picker is hidden or disabled, no row is
  // highlighted, or the highlighted row is already the selection.
  function selectFirstResult() {
    if (disabled.value || !menuShown.value) {
      return null;
    }

    const committed = container.value?.selectFirstResult?.({
      markedOnly: true,
    });

    // The picker reports its rendered row, a decorated copy carrying the hint.
    return committed ? (findItem(getItemValue(committed)) ?? committed) : null;
  }

  // The pick's own close applies (none under `keep-open-on-select`). Without a commit, Enter closes
  // the picker.
  function handleActivatorEnter(event) {
    if (isComposing(event)) {
      return;
    }

    if (fieldCommitsHint.value && selectFirstResult()) {
      event.preventDefault();

      if (!keepOpenOnSelect.value) {
        closeMenu();
      }

      return;
    }

    if (menuOpen.value) {
      menuOpen.value = false;
    }
  }

  function handleActivatorTab(event) {
    if (!isComposing(event) && !event.shiftKey && fieldCommitsHint.value) {
      selectFirstResult();
    }
  }

  // Carries the search bar's keyboard event.
  function handleSearchEnter(hostEvent) {
    const { detail } = hostEvent;

    const [event] = detail;

    if (commitsFirstResult.value && !(event && isComposing(event))) {
      selectFirstResult();
    }
  }

  return {
    fieldCommitsHint,
    selectFirstResult,
    handleActivatorEnter,
    handleActivatorTab,
    handleSearchEnter,
  };
}
